import { baseForPdf } from '../apiClients';
import { fetchAndCacheWithoutPage } from '../../components/shared/CacheStroage/CacheWithoutPage';
import { Endpoints } from '../../network';

export const getBookLandingPageData = async (slug: string) => {
  const apiUrl = `${Endpoints.getLandingPageData}/${slug}`;

  const apiCall = async () => {
    const response = await baseForPdf.get(apiUrl);
    return response?.data;
  };

  return fetchAndCacheWithoutPage(apiCall, apiUrl);
};

export const getCourseLandingPageData = async (slug: string) => {
  const apiUrl = `${Endpoints.getCourseLandingPageData}/${slug}`;

  const apiCall = async () => {
    const response = await baseForPdf.get(apiUrl);
    return response?.data;
  };

  return fetchAndCacheWithoutPage(apiCall, apiUrl);
};

export const getCourseDataSSR = async (slug: string) => {
  const urlWithQuery = `${Endpoints.getCourseLandingPageData}/${slug}`;
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_READER_URL}${urlWithQuery}`,
      { cache: 'force-cache' },
    );

    if (!response.ok) {
      throw new Error(
        `Error fetching course landing page details: ${response.statusText} `,
      );
    }

    const data = await response.json();
    return data?.data;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

export const getBookLandingPageDataSSR = async (slug: string) => {
  const urlWithQuery = `${Endpoints.getLandingPageData}/${slug}`;
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_READER_URL}${urlWithQuery}`,
      { cache: 'force-cache' },
    );
    if (response.status === 404) {
      throw { code: 404 };
    }

    if (!response.ok) {
      throw new Error(
        `Error fetching book landing page details: ${response.statusText} `,
      );
    }

    const data = await response.json();
    return data?.data;
  } catch (error) {
    console.error('Fetch error landing page:', error);
    throw error;
  }
};

export const getBooDataByIdSSR = async (id: string, token: string) => {
  const urlWithQuery = `${Endpoints.getAllBooks}/${id}`;
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_READER_URL}/${urlWithQuery}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },

        cache: 'force-cache',
      },
    );
    if (response.status === 404) {
      throw { code: 404 };
    }

    if (!response.ok) {
      throw new Error(
        `Error fetching book landing page details: ${response.statusText} `,
      );
    }

    const data = await response.json();
    return data?.data;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

export const getProductDataSSR = async (
  productId: string | number,
  country: string,
  token?: string,
) => {
  const url = `${Endpoints.productDetail}/${productId}`;

  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}${url}`, {
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        country: country,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (response.status === 404) {
      throw { code: 404 };
    }

    if (!response.ok) {
      throw new Error(`Error fetching product data: ${response.statusText}`);
    }

    const data = await response.json();
    return data?.data;
  } catch (error) {
    console.error('Product fetch error:', error);
    throw error;
  }
};

export const getLandingPageSeoSSR = async (productId: string) => {
  const urlWithQuery = `${Endpoints.getLandingPageSeoData}/${productId}`;

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_READER_URL}${urlWithQuery}`,
      { cache: 'force-cache' },
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data?.data ?? null;
  } catch (error) {
    console.error('Fetch error SEO:', error);
    return null;
  }
};
export const getAllProductsSSR = async ({
  category_id,
  page = 1,
  book_type,
  title,
  limit,
  is_preorder,
  country,
}: {
  category_id: number;
  page?: number;
  book_type?: string;
  title?: string;
  limit?: number;
  is_preorder?: boolean;
  country: string;
}) => {
  try {
    const queryParams = new URLSearchParams({
      category_id: category_id.toString(),
      page: page.toString(),
      ...(book_type && { book_type }),
      ...(title?.trim() && { name: title.trim() }),
      ...(limit && { perPage: limit.toString() }),
      ...(typeof is_preorder !== 'undefined' && {
        is_preorder: is_preorder.toString(),
      }),
    });

    const url = `${process.env.NEXT_PUBLIC_API_URL}${Endpoints.getAllProducts}?${queryParams}`;

    const response = await fetch(url, {
      cache: 'force-cache',
      headers: {
        'Content-Type': 'application/json',
        country: country,
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch products:', response.status);
      return null;
    }

    const data = await response.json();
    return data?.data ?? null;
  } catch (error) {
    console.error('Fetch error:', error);
    return null;
  }
};

export const getBookRecourses = async (productId: string, token: string) => {
  const urlWithQuery = `${Endpoints.getBookRecourses}/${productId}`;
  const fullUrl = `${process.env.NEXT_PUBLIC_API_READER_URL}${urlWithQuery}`;

  try {
    const response = await fetch(fullUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'force-cache',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    return data?.data;
  } catch (error) {
    console.error('[getBookRecourses] Fetch error:', error);
  }
};

export const getCategoryByNameSSR = async (name: string) => {
  const urlWithQuery = `${Endpoints.getBookCategoriesByName}/${name}`;

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_READER_URL}${urlWithQuery}`,
      { cache: 'force-cache' },
    );

    if (!response.ok) {
      console.warn(`Category fetch failed (${response.status})`);
      return null; // ✅ return null instead of throwing
    }

    const data = await response.json();
    return data?.data || null;
  } catch (error) {
    console.error('Fetch error (getCategoryByNameSSR):', error);
    return null; // ✅ fallback instead of throw
  }
};

export const getCourseLandingPageDataSSR = async (slug: string) => {
  const urlWithQuery = `${Endpoints.getCourseLandingPageData}/${slug}`;
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_READER_URL}${urlWithQuery}`,
      { cache: 'force-cache' },
    );

    if (!response.ok) {
      throw new Error(
        `Error fetching book landing page details: ${response.statusText} `,
      );
    }

    const data = await response.json();
    return data?.data;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

export const toggleReviewLike = async (reviewId: string) => {
  try {
    const response = await baseForPdf.post(
      `${Endpoints.getAllProductReviews}/${reviewId}/like`,
    );
    return response.data;
  } catch (error) {
    console.error('Error toggling like on review:', error);
    throw error;
  }
};

export const toggleReviewDislike = async (reviewId: string) => {
  try {
    const response = await baseForPdf.post(
      `${Endpoints.getAllProductReviews}/${reviewId}/dislike`,
    );
    return response.data;
  } catch (error) {
    console.error('Error toggling dislike on review:', error);
    throw error;
  }
};
